import { useRef, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { mediaUrl } from "@/lib/api";

export default function CategoryCarousel({ categories = [], variant = "default", autoplay = false, autoplayInterval = 4200 }) {
  const ref = useRef(null);
  const drag = useRef({ down: false, startX: 0, scroll: 0, moved: false });
  const paused = useRef(false);
  const [grabbing, setGrabbing] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [active, setActive] = useState(0);

  const updateEdges = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
    // active index = card whose left edge is closest to container left
    const cRect = el.getBoundingClientRect();
    let best = 0, bestDist = Infinity;
    Array.from(el.children).forEach((child, i) => {
      const d = Math.abs(child.getBoundingClientRect().left - cRect.left);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    setActive(best);
  }, []);

  useEffect(() => { updateEdges(); }, [categories, updateEdges]);

  const goTo = useCallback((index) => {
    const el = ref.current;
    if (!el || !el.children[index]) return;
    const child = el.children[index];
    const target = child.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft;
    el.scrollTo({ left: target, behavior: "smooth" });
  }, []);

  const scrollBy = (dir) => {
    const el = ref.current;
    if (!el) return;
    const amount = Math.min(el.clientWidth * 0.8, 520);
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  // Autoplay
  useEffect(() => {
    if (!autoplay || categories.length <= 1) return;
    const id = setInterval(() => {
      const el = ref.current;
      if (!el || paused.current) return;
      const isEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
      if (isEnd) el.scrollTo({ left: 0, behavior: "smooth" });
      else scrollBy(1);
    }, autoplayInterval);
    return () => clearInterval(id);
  }, [autoplay, autoplayInterval, categories.length]);

  const pause = () => { paused.current = true; };
  const resume = () => { paused.current = false; };

  const onPointerDown = (e) => {
    pause();
    if (e.pointerType && e.pointerType !== "mouse") return; // touch uses native scroll
    const el = ref.current;
    if (!el) return;
    drag.current = { down: true, startX: e.clientX, scroll: el.scrollLeft, moved: false };
    setGrabbing(true);
  };
  const onPointerMove = (e) => {
    if (!drag.current.down) return;
    if (e.pointerType && e.pointerType !== "mouse") return;
    const el = ref.current;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    el.scrollLeft = drag.current.scroll - dx;
  };
  const endDrag = () => { drag.current.down = false; setGrabbing(false); };

  if (!categories.length) return null;
  const glass = variant === "glass";
  const arrowCls = glass
    ? "border-white/40 text-white hover:bg-white hover:text-sunset-ink disabled:hover:bg-transparent disabled:hover:text-white"
    : "border-sunset-border hover:bg-sunset-ink hover:text-white hover:border-sunset-ink disabled:hover:bg-transparent disabled:hover:text-sunset-ink";

  return (
    <div className="relative" onMouseEnter={pause} onMouseLeave={resume}>
      <div className="hidden md:flex items-center justify-end gap-2 mb-5">
        <button onClick={() => scrollBy(-1)} disabled={atStart} data-testid="carousel-prev"
          className={`w-11 h-11 border flex items-center justify-center transition-colors disabled:opacity-30 ${arrowCls}`}>
          <ArrowLeft size={18} />
        </button>
        <button onClick={() => scrollBy(1)} disabled={atEnd} data-testid="carousel-next"
          className={`w-11 h-11 border flex items-center justify-center transition-colors disabled:opacity-30 ${arrowCls}`}>
          <ArrowRight size={18} />
        </button>
      </div>

      <div
        ref={ref}
        onScroll={updateEdges}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onTouchStart={pause}
        onTouchEnd={resume}
        data-testid="category-carousel"
        className={`no-scrollbar snap-x-strip flex gap-5 overflow-x-auto pb-4 cursor-grab ${grabbing ? "grabbing" : ""}`}
      >
        {categories.map((c, i) => (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
            className="shrink-0 w-[78vw] sm:w-[340px] lg:w-[380px]"
          >
            <Link
              to={`/categoria/${c.slug}`}
              data-testid={`carousel-category-${c.slug}`}
              draggable={false}
              onClick={(e) => { if (drag.current.moved) e.preventDefault(); }}
              className={`group relative block aspect-[3/4] overflow-hidden bg-sunset-ink ${glass ? "ring-1 ring-white/25 shadow-2xl" : ""}`}
            >
              {c.image && (
                <img src={mediaUrl(c.image)} alt={c.name} draggable={false}
                  className={`w-full h-full object-cover group-hover:scale-105 transition-all duration-700 ${glass ? "opacity-60" : "opacity-75 group-hover:opacity-100"}`} />
              )}
              <div className={`absolute inset-0 ${glass ? "bg-black/20 backdrop-blur-[2px] group-hover:backdrop-blur-0 transition-all duration-500" : ""}`} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10" />

              <span className="absolute top-6 left-6 font-serif text-white/25 text-6xl leading-none group-hover:text-sunset-accent/60 transition-colors duration-500">
                {String(i + 1).padStart(2, "0")}
              </span>

              <div className="absolute inset-x-4 bottom-4 flex flex-col justify-end p-4 rounded-sm">
                {glass && <div className="absolute inset-0 bg-white/10 backdrop-blur-md border border-white/20 rounded-sm" />}
                <div className="relative">
                  <span className="kicker text-white/80 mb-3 flex items-center gap-2">
                    Explorar <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </span>
                  <h3 className="font-serif text-white text-3xl lg:text-[2rem] leading-none tracking-tight">{c.name}</h3>
                  <span className="block h-px w-0 group-hover:w-16 bg-sunset-accent mt-4 transition-all duration-500" />
                  {c.description && <p className="text-white/70 text-sm mt-3 line-clamp-2">{c.description}</p>}
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Dots navigation */}
      <div className="mt-6">
        <div className={`inline-flex items-center gap-2 ${glass ? "bg-black/25 backdrop-blur-md px-4 py-2.5 rounded-full" : ""}`} data-testid="carousel-dots">
          {categories.map((c, i) => (
            <button
              key={c.id}
              onClick={() => goTo(i)}
              aria-label={`Ir a ${c.name}`}
              data-testid={`carousel-dot-${i}`}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === active
                  ? `w-8 ${glass ? "bg-white" : "bg-sunset-accent"}`
                  : `w-2 ${glass ? "bg-white/50 hover:bg-white/80" : "bg-sunset-border hover:bg-sunset-muted"}`
              }`}
            />
          ))}
        </div>
      </div>

      <div className={`md:hidden mt-2 flex items-center gap-2 ${glass ? "text-white/80" : "text-sunset-muted"}`}>
        <span className="kicker text-[10px]">Desliza</span>
        <ArrowRight size={14} className="animate-pulse" />
      </div>
    </div>
  );
}
